import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import closeIcon from "../../assets/close-icon.png";
import checkIcon from "../../assets/check-filled-icon.png";
import jsonAnimation from "../../assets/json-animation.gif";
import { CheckCircle, AlertTriangle, Minimize2, Maximize2 } from 'lucide-react';
import {
  toggleCollapseFiles,
  setStepsCompleted,
  setCurrentlyProcessingSession,
  switchWorkflowSession,
  updateWorkflowSessionState,
  resetDocumentsState,
} from '../../store/reducers';
import { loadWorkflowLastJobId } from '../../utils/workflowSessionStorage';
import { useDeclarationSummary } from '../../hooks/useDeclarationSummary';
import { useLanguage, getProcessingModalTranslation } from '../../hooks/useLanguage';
import { apiFetch } from '../../utils/api';
import { JobStreamDisplay } from './JobStreamDisplay';
import { handleProcessingStatusChange } from '../../utils/processingStatusUtils';
import { tryDispatchProcessNextInQueue } from '../../utils/workflowStreamStatusUtils';
import { clearWorkflowFiles } from '../../utils/workflowFileCache';
import { 
  cancelJob, 
  handleCancelProcessing, 
  handleCloseModal, 
  resetProcessingStates,
  resetModalStateOnWorkflowChange,
  resetModalStateOnProcessingStart,
  getWorkflowStepsCompleted,
  setWorkflowStepsCompleted,
  initializeModalCompletionState,
  getWorkflowProcessingState,
  getComprehensiveWorkflowState,
  getWorkflowDataState,
  getWorkflowStepState,
  shouldShowProcessingModal,
  handleStreamError,
  handleJobError,
} from '../../utils/processingModalUtils';

export const ProcessingModal = ({ 
  isProcessing, 
  setIsProcessing, 
  onProcessComplete, 
  onCancelProcessing,
  workflowSessionId = null, // Optional: specific workflow session ID this modal belongs to
  forceMinimized = false, // If true, always render in minimized mode
  initialJobId = null, // Optional: initial jobId to use for this workflow
  /** When true, MultiProcessingModalManager owns the only JobStreamDisplay for this job */
  useExternalJobStream = false,
}) => {
  const [isSpanish] = useLanguage();
  const processingSteps = [
    getProcessingModalTranslation('addingFiles', isSpanish),
    getProcessingModalTranslation('processingDocuments', isSpanish),
    getProcessingModalTranslation('processingComplete', isSpanish)
  ];

  // Initialize currentStep based on whether we have a jobId If jobId exists, we're already past "adding files" step
  const [currentStep, setCurrentStep] = useState(initialJobId ? 1 : 0);
  const [completedSteps, setCompletedSteps] = useState(initialJobId ? [0] : []);
  const [areStepsCompleted, setAreStepsCompleted] = useState(false);
  const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [processingStatus, setProcessingStatus] = useState(initialJobId ? "processing" : "");
  const [jobId, setJobId] = useState(initialJobId);
  const [countdown, setCountdown] = useState(10);
  const countdownTimerRef = useRef(null);
  const handleCloseRef = useRef(null);
  const [isThinking, setIsThinking] = useState(false);
  const dispatch = useDispatch();

  // Track if we've already dispatched processNextInQueue to prevent duplicate dispatches
  const hasDispatchedQueueEventRef = useRef(false);
  
  // Track if we've already handled an error to prevent infinite loops
  const errorHandledRef = useRef(false);
  
  // Track if user has manually maximized an inactive workflow
  const userMaximizedInactiveRef = useRef(false);

  // Get workflow session info
  const activeWorkflowSessionId = useSelector(state => state.workflowSessions?.activeSessionId);
  const workflowSessions = useSelector(state => state.workflowSessions);
  
  // Use provided workflowSessionId or fall back to active workflow
  const modalWorkflowSessionId = workflowSessionId || activeWorkflowSessionId;
  const workflowSession = modalWorkflowSessionId ? workflowSessions?.sessions?.[modalWorkflowSessionId] : null;
  
  // Get minimized state from workflow session state (per-workflow)
  const workflowModalMinimized = workflowSession?.state?.modalMinimized;
  const [isMinimizedLocal, setIsMinimizedLocal] = useState(
    workflowModalMinimized !== undefined ? workflowModalMinimized : forceMinimized
  );
  
  // Sync local state with Redux state
  useEffect(() => {
    if (workflowModalMinimized !== undefined) {
      setIsMinimizedLocal(workflowModalMinimized);
    }
  }, [workflowModalMinimized]);
  
  // Wrapper that updates both local state and Redux
  const setIsMinimized = useCallback((value) => {
    setIsMinimizedLocal(value);
    if (modalWorkflowSessionId && dispatch) {
      dispatch(updateWorkflowSessionState(modalWorkflowSessionId, {
        modalMinimized: value,
      }));
    }
  }, [modalWorkflowSessionId, dispatch]);
  
  // Alias for backward compatibility with utility functions
  const handleSetIsMinimized = setIsMinimized;
  
  // Use local state for rendering
  const isMinimized = isMinimizedLocal;
  const workflowName = workflowSession?.name || (modalWorkflowSessionId ? `Workflow ${modalWorkflowSessionId.replace('workflow-', '')}` : 'Workflow');
  const isActiveWorkflow = modalWorkflowSessionId === activeWorkflowSessionId;
  
  // Get completion state from workflow session state (persisted state)
  const workflowStepsCompleted = workflowSession?.state?.stepsCompleted || false;
  
  // CRITICAL: Get modalClosed state to track if user has explicitly closed this modal
  const modalClosed = workflowSession?.state?.modalClosed || false;

  const jsonData = useSelector((state) => state.documents.extractedData);

  const {
    saveChanges
  } = useDeclarationSummary(jsonData);

  // Track previous areStepsCompleted to detect changes
  const areStepsCompletedRef = useRef(false);
  
  // Track if workflow was already completed when modal opened
  const workflowWasCompletedRef = useRef(false);

  // Initialize completion state from workflow session when modal opens or workflow changes
  useEffect(() => {
    if (modalWorkflowSessionId && workflowStepsCompleted) {
      const wasCompleted = initializeModalCompletionState(
        modalWorkflowSessionId,
        setAreStepsCompleted,
        setCurrentStep,
        setCompletedSteps
      );
      const wasAlreadyCompleted = areStepsCompletedRef.current;
      workflowWasCompletedRef.current = wasCompleted;
      areStepsCompletedRef.current = wasCompleted;
      
      // CRITICAL: If workflow is completed in Redux but modal state wasn't updated, update it now This handles the case where workflow completed while modal was minimized
      if (wasCompleted && !wasAlreadyCompleted) {
        setIsProcessing(false);
        setProcessingStatus('completed');
        setCurrentStep(2);
        setCompletedSteps([0, 1, 2]);
        
        // If minimized and workflow completed, maximize it (if it's the active workflow)
        if (isMinimizedLocal && (isActiveWorkflow || !forceMinimized)) {
          setIsMinimized(false);
        }
      }
    } else {
      workflowWasCompletedRef.current = false;
    }
  }, [modalWorkflowSessionId, workflowStepsCompleted, isMinimizedLocal, isActiveWorkflow, forceMinimized, setIsMinimized]);

  // When the parent owns the SSE (hidden JobStreamDisplay), mirror step + status from workflow session state
  useEffect(() => {
    if (!useExternalJobStream || !modalWorkflowSessionId) return;
    const st = workflowSession?.state;
    if (!st) return;

    if (typeof st.currentStep === 'number' && st.currentStep !== currentStep) {
      setCurrentStep(st.currentStep);
    }
    if (Array.isArray(st.completedSteps)) {
      const next = st.completedSteps;
      if (next.length !== completedSteps.length || next.some((v, i) => v !== completedSteps[i])) {
        setCompletedSteps(next);
      }
    }
    if (st.stepsCompleted && processingStatus !== 'completed' && processingStatus !== 'error') {
      setProcessingStatus('completed');
    }
  }, [
    useExternalJobStream,
    modalWorkflowSessionId,
    workflowSession?.state?.currentStep,
    workflowSession?.state?.completedSteps,
    workflowSession?.state?.stepsCompleted,
    currentStep,
    completedSteps,
    processingStatus,
  ]);
  
  // Memoize the status update callback to prevent infinite loops
  const handleStatusUpdate = useCallback((statusUpdate) => {
    if (statusUpdate.areStepsCompleted !== undefined) {
      const wasCompleted = areStepsCompletedRef.current;
      const isNowCompleted = statusUpdate.areStepsCompleted;
      
      setAreStepsCompleted(isNowCompleted);
      areStepsCompletedRef.current = isNowCompleted;
      
      // Persist completion state in workflow session when steps complete
      if (isNowCompleted && !wasCompleted && modalWorkflowSessionId) {
        console.log(`🎉 [Workflow:${modalWorkflowSessionId}] Workflow completed! Steps are now completed.`);
        
        // Get current queue state before updating
        const store = require('../../store/store').default || require('../../store/store');
        const currentState = store.getState();
        const queue = currentState?.workflowSessions?.processingQueue || [];
        console.log(`📋 [Workflow:${modalWorkflowSessionId}] Queue state at completion:`, {
          completedWorkflow: modalWorkflowSessionId,
          queueLength: queue.length,
          queue: queue,
          nextInQueue: queue[0] || 'none'
        });
        
        setWorkflowStepsCompleted(modalWorkflowSessionId, true, dispatch);
        workflowWasCompletedRef.current = true;
        // CRITICAL: Clear processing state when completed
        setIsProcessing(false);
        dispatch(updateWorkflowSessionState(modalWorkflowSessionId, {
          isProcessing: false,
          stepsCompleted: true,
        }));
        
        // PERFORMANCE: Clear PDFs from workflow cache after processing completes
        // This frees up memory since PDFs are no longer needed after processing
        clearWorkflowFiles(modalWorkflowSessionId);
        
        // PERFORMANCE: Clear Redux documents state (uploadedFiles) if this is the active workflow
        // This frees up memory since PDFs are stored in Redux state and no longer needed after processing
        if (isActiveWorkflow) {
          dispatch(resetDocumentsState());
        }
        
        // CRITICAL: Always clear currentlyProcessingSessionId when workflow completes This must happen BEFORE dispatching the queue event to ensure it's cleared
        dispatch(setCurrentlyProcessingSession(null));
        
        // CRITICAL: Set processingStatus to 'completed' to trigger processNextInQueue event This ensures the queue processing event is dispatched even if JobStreamDisplay only sends areStepsCompleted without processingStatus
        if (statusUpdate.processingStatus !== 'completed') {
          if (process.env.NODE_ENV === 'development') {
            console.log(`🔄 [Workflow:${modalWorkflowSessionId}] Setting processingStatus to 'completed' to trigger queue processing`);
          }
          setProcessingStatus('completed');
        } else {
          tryDispatchProcessNextInQueue(modalWorkflowSessionId);
          hasDispatchedQueueEventRef.current = true;
        }
      }
      
      // When stream completes, maximize the modal immediately (if it's the active workflow) For inactive workflows (forceMinimized), we'll let the useEffect handle it
      if (isNowCompleted && !wasCompleted) {
        // Only auto-maximize if it's the active workflow or not force minimized
        if (isActiveWorkflow || !forceMinimized) {
          setIsMinimized(false);
        }
      }
    }
    if (statusUpdate.currentStep !== undefined) {
      setCurrentStep(statusUpdate.currentStep);
    }
    if (statusUpdate.completedSteps !== undefined) {
      setCompletedSteps(statusUpdate.completedSteps);
    }
    if (statusUpdate.processingStatus !== undefined) {
      setProcessingStatus(statusUpdate.processingStatus);
      if (statusUpdate.processingStatus === 'error') {
        // Use centralized error handler
        handleStreamError({
          workflowSessionId: modalWorkflowSessionId,
          error: statusUpdate.error || 'An error occurred during document processing.',
          setHasError,
          setErrorMessage,
          setIsProcessing,
          setIsMinimized,
          dispatch,
        });
      }
    }
    if (statusUpdate.error && !statusUpdate.processingStatus) {
      // Use centralized error handler
      handleStreamError({
        workflowSessionId: modalWorkflowSessionId,
        error: statusUpdate.error,
        setHasError,
        setErrorMessage,
        setIsProcessing,
        setIsMinimized,
        dispatch,
      });
    }
    if (statusUpdate.jobId && statusUpdate.jobId !== jobId) {
      setJobId(statusUpdate.jobId);
      window.dispatchEvent(new CustomEvent('setActiveJobId', { 
        detail: { 
          jobId: statusUpdate.jobId,
          workflowSessionId: modalWorkflowSessionId 
        } 
      }));
    }
    // Check if stream is thinking
    if (statusUpdate.isThinking !== undefined) {
      setIsThinking(statusUpdate.isThinking);
    }
  }, [jobId, workflowSessions, activeWorkflowSessionId, dispatch, isActiveWorkflow, forceMinimized, setIsMinimized, setIsProcessing]);

  // Store latest values in refs to avoid stale closures
  const isProcessingRef = useRef(isProcessing);
  const modalWorkflowSessionIdRef = useRef(modalWorkflowSessionId);
  
  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);
  
  useEffect(() => {
    modalWorkflowSessionIdRef.current = modalWorkflowSessionId;
  }, [modalWorkflowSessionId]);

  const handleProcessDocuments = useCallback(async () => {
    if (!isProcessingRef.current || isApiCallInProgress) {
      return;
    }

    // Reset states at the beginning of processing using utility
    resetProcessingStates({
      setHasError,
      setErrorMessage,
      setCurrentStep,
      setCompletedSteps,
      setAreStepsCompleted,
      setProcessingStatus,
      setIsThinking,
      setIsApiCallInProgress,
      dispatch,
      errorHandledRef,
    });

    try {
      // Call the document processing function with status update callback
      const responseData = await onProcessComplete((status) => {
        if (status.areStepsCompleted !== undefined) {
          setAreStepsCompleted(status.areStepsCompleted);
        }
        if (status.currentStep !== undefined) {
          setCurrentStep(status.currentStep);
        }
        if (status.completedSteps !== undefined) {
          setCompletedSteps(status.completedSteps);
        }
        // Handle rate limit exceeded status
        if (status.status === 'rate_limit_exceeded' || status.processingStatus === 'rate_limit_exceeded') {
          // Stop processing when rate limit is exceeded
          setIsProcessing(false);
          setProcessingStatus('rate_limit_exceeded');
          // Don't set error - let RateLimitExceededModal handle it
        }
        if (status.processingStatus !== undefined) {
          setProcessingStatus(status.processingStatus);
          // Handle error status from callback
          if (status.processingStatus === 'error') {
            setHasError(true);
            setErrorMessage(status.error || 'An error occurred during document processing.');
            // Stop processing when error occurs
            setIsProcessing(false);
            // Maximize modal when error occurs
            setIsMinimized(false);
          }
        }
        if (status.error && !status.processingStatus) {
          // Handle error even if processingStatus is not set
          setHasError(true);
          setErrorMessage(status.error);
          // Stop processing when error occurs
          setIsProcessing(false);
          // Maximize modal when error occurs
          setIsMinimized(false);
        }
        if (status.jobId) {
          setJobId(status.jobId);
          // Dispatch event to Dashboard to keep stream open CRITICAL: Include workflowSessionId to ensure job ID is saved to the correct workflow
          window.dispatchEvent(new CustomEvent('setActiveJobId', { 
            detail: { 
              jobId: status.jobId,
              workflowSessionId: modalWorkflowSessionIdRef.current 
            } 
          }));
        }
      });
      
      // If we have a response with a jobId
      if (responseData?.jobId) {
        const newJobId = responseData.jobId;
        
        // Handle "already have a job" error if it occurs (shouldn't with optimized backend)
        if (responseData.status === 'error' && responseData.message?.includes('already have a job in the queue')) {
          console.error(`❌ [ProcessingModal] Backend error: "already have a job in queue"`, {
            workflowSessionId: modalWorkflowSessionIdRef.current,
            message: responseData.message,
            code: responseData.code,
            status: responseData.status
          });
        }
        
        if (newJobId) {
          setJobId(newJobId);
          window.dispatchEvent(new CustomEvent('setActiveJobId', { 
            detail: { 
              jobId: newJobId,
              workflowSessionId: modalWorkflowSessionIdRef.current 
            } 
          }));
        }
        setCompletedSteps([0]);
        setCurrentStep(1);
        setProcessingStatus("processing");
      } else {
        // Use centralized error handler
        handleJobError({
          workflowSessionId: modalWorkflowSessionIdRef.current,
          responseData: responseData || {},
          statusCode: responseData?.statusCode || 0,
          setHasError,
          setErrorMessage,
          setIsProcessing,
          setIsMinimized,
          dispatch,
        });
        setCompletedSteps([]);
        setProcessingStatus("error");
        processingTriggeredRef.current = false;
      }
    } catch (error) {
      if (error.message === 'WORKFLOW_QUEUED' && error.queueAdded) {
        setIsProcessing(false);
        setIsApiCallInProgress(false);
        processingTriggeredRef.current = false;
        return;
      }
      
      // Use centralized error handler
      handleJobError({
        workflowSessionId: modalWorkflowSessionIdRef.current,
        responseData: { error: error.message },
        statusCode: error.status || 0,
        setHasError,
        setErrorMessage,
        setIsProcessing,
        setIsMinimized,
        dispatch,
      });
      setCompletedSteps([]);
      setProcessingStatus("error");
      processingTriggeredRef.current = false;
    } finally {
      setIsApiCallInProgress(false);
    }
  }, [isApiCallInProgress, onProcessComplete, setIsProcessing, dispatch]);

  // Track if we've already triggered processing for this isProcessing=true state
  const processingTriggeredRef = useRef(false);
  
  // Reset queue event dispatch flag when processing starts or workflow changes
  useEffect(() => {
    if (isProcessing && !processingTriggeredRef.current) {
      hasDispatchedQueueEventRef.current = false;
    }
  }, [isProcessing]);
  
  // Reset queue event dispatch flag when workflow session changes (new workflow)
  useEffect(() => {
    hasDispatchedQueueEventRef.current = false;
  }, [modalWorkflowSessionId]);
  
  // CRITICAL: Reset modal state when workflow changes (for queue processing) This ensures steps are refreshed when a new workflow starts processing
  const prevWorkflowSessionIdRef = useRef(modalWorkflowSessionId);
  useEffect(() => {
    // If workflow session ID changed, reset all modal state
    if (prevWorkflowSessionIdRef.current !== null && 
        prevWorkflowSessionIdRef.current !== modalWorkflowSessionId) {
      console.log(`[Workflow:${modalWorkflowSessionId}] Workflow changed, resetting modal state:`, {
        from: prevWorkflowSessionIdRef.current,
        to: modalWorkflowSessionId
      });
      
      // Use utility function to reset modal state
      resetModalStateOnWorkflowChange({
        setHasError,
        setErrorMessage,
        setCurrentStep,
        setCompletedSteps,
        setAreStepsCompleted,
        setProcessingStatus,
        setIsThinking,
        setJobId,
        setIsMinimized,
        dispatch,
        errorHandledRef,
        processingTriggeredRef,
        hasDispatchedQueueEventRef,
        userMaximizedInactiveRef,
        initialJobId,
        forceMinimized,
      });
    }
    
    prevWorkflowSessionIdRef.current = modalWorkflowSessionId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalWorkflowSessionId, initialJobId, forceMinimized]);

  // Listen for queue processing start events (to help with state synchronization)
  useEffect(() => {
    const handleQueueProcessingStart = (event) => {
      const eventWorkflowSessionId = event.detail?.workflowSessionId;
      // Only respond if this event is for this modal's workflow
      if (eventWorkflowSessionId === modalWorkflowSessionId) {
        console.log(`🚀 [Workflow:${modalWorkflowSessionId}] Received startQueueProcessing event - will start processing now`);
        // Reset trigger flag to allow processing to start
        processingTriggeredRef.current = false;
      }
    };
    
    window.addEventListener('startQueueProcessing', handleQueueProcessingStart);
    return () => {
      window.removeEventListener('startQueueProcessing', handleQueueProcessingStart);
    };
  }, [modalWorkflowSessionId, isProcessing]);
  
  // Track previous isProcessing state to detect transitions
  const prevIsProcessingRef = useRef(isProcessing);
  
  // This effect runs when isProcessing changes
  useEffect(() => {
    const wasProcessing = prevIsProcessingRef.current;
    const isNowProcessing = isProcessing;
    prevIsProcessingRef.current = isProcessing;
    
    // CRITICAL: Removed console.log that was causing infinite loops on every render/stream update Uncomment only for debugging specific issues console.log(`[Workflow:${modalWorkflowSessionId}] isProcessing effect triggered:`, { isProcessing, wasProcessing, hasError, processingTriggered: processingTriggeredRef.current, isApiCallInProgress, hasDocuments: workflowSession?.state?.documents?.length || 0 });
    
    // Reset modal state when processing starts (false -> true transition)
    if (!wasProcessing && isNowProcessing && !hasError) {
      resetModalStateOnProcessingStart({
        setHasError,
        setErrorMessage,
        setCurrentStep,
        setCompletedSteps,
        setAreStepsCompleted,
        setProcessingStatus,
        setIsThinking,
        setJobId,
        setIsMinimized,
        dispatch,
        errorHandledRef,
        processingTriggeredRef,
        hasDispatchedQueueEventRef,
        userMaximizedInactiveRef,
        initialJobId,
        forceMinimized,
        modalWorkflowSessionId,
      });
    }
    
    // Reset trigger flag when isProcessing becomes false OR when there's an error
    if (!isProcessing || hasError) {
      if (processingTriggeredRef.current) {
        processingTriggeredRef.current = false;
      }
      
      if (hasError && isProcessing) {
        setIsProcessing(false);
      }
      
      return;
    }
    
    // Skip if we've already triggered processing for this state
    if (processingTriggeredRef.current) {
      return;
    }
    
    // Skip if already processing API call
    if (isApiCallInProgress) {
      return;
    }
    
    const hasDocuments = workflowSession?.state?.documents && workflowSession.state.documents.length > 0;
    const hasJobId = !!jobId || !!initialJobId;
    
    // Don't start processing if workflow is already completed
    if (workflowStepsCompleted || areStepsCompleted) {
      setIsProcessing(false);
      return;
    }
    
    // If there's a jobId, we're restoring a previous job - stream will handle it
    if (hasJobId) {
      return;
    }
    
    // Only process if there are documents AND no jobId (meaning it's a new processing request)
    if (hasDocuments) {
      processingTriggeredRef.current = true;
      handleProcessDocuments();
    } else {
      setHasError(true);
      setErrorMessage("No PDF files uploaded");
      setProcessingStatus("error");
      setIsProcessing(false);
      processingTriggeredRef.current = false;
    }
  }, [isProcessing, workflowSession, isApiCallInProgress, hasError, handleProcessDocuments, setIsProcessing, modalWorkflowSessionId, isActiveWorkflow, jobId, initialJobId, workflowStepsCompleted, areStepsCompleted]);

  const handleRetry = () => {
    if (!isApiCallInProgress) {
      handleProcessDocuments();
    }
  };

  const handleClose = useCallback(async () => {
    handleCloseModal({
      jobId,
      onCancelProcessing,
      setAreStepsCompleted,
      setIsProcessing,
      dispatch,
      saveChanges,
      modalWorkflowSessionId,
      workflowWasCompleted: workflowWasCompletedRef.current || areStepsCompleted, // Pass completion state
    });
  }, [jobId, onCancelProcessing, dispatch, saveChanges, modalWorkflowSessionId, areStepsCompleted, isActiveWorkflow]);

  // Keep handleClose ref updated
  useEffect(() => {
    handleCloseRef.current = handleClose;
  }, [handleClose]);

  const handleCancel = async () => {
    // Use utility function to handle cancel processing
    await handleCancelProcessing({
      jobId,
      onCancelProcessing,
      setIsProcessing,
      setCurrentStep,
      setCompletedSteps,
      setAreStepsCompleted,
      setProcessingStatus,
      setJobId,
      dispatch,
      modalWorkflowSessionId, // CRITICAL: Pass workflow session ID to clear jobId and set modalClosed
    });
  };

  const handleCancelJob = async () => {
    // Use utility function to cancel job
    await cancelJob({
      jobId,
      setIsProcessing,
      setJobId,
    });
  };

  // Track previous processingStatus to prevent re-running same status
  const prevProcessingStatusRef = useRef(processingStatus);
  
  // Update steps based on processing status
  useEffect(() => {
    // Only run if processingStatus actually changed
    if (prevProcessingStatusRef.current === processingStatus) {
      return;
    }
    
    // Don't process status changes if workflow is already completed
    if (workflowStepsCompleted && processingStatus === 'processing') {
      prevProcessingStatusRef.current = processingStatus;
      return;
    }
    
    const prevStatus = prevProcessingStatusRef.current;
    prevProcessingStatusRef.current = processingStatus;
    
    // Use utility function to handle processing status change
    const result = handleProcessingStatusChange({
      processingStatus,
      prevProcessingStatus: prevStatus,
      modalWorkflowSessionId,
      setAreStepsCompleted,
      setCurrentStep,
      setCompletedSteps,
      setHasError,
      setIsProcessing,
      setIsMinimized,
      dispatch,
      workflowSessions,
      hasDispatchedQueueEventRef,
      errorHandledRef,
    });
    
    if (!result.statusHandled && processingStatus === 'error' && !errorHandledRef.current) {
      errorHandledRef.current = true;
      setHasError(true);
      setIsProcessing(false);
      setIsMinimized(false);
    }
  }, [processingStatus, dispatch, modalWorkflowSessionId, jobId, workflowSessions]);

  // Countdown timer when steps are completed CRITICAL: Run countdown for ANY workflow that has completed steps (both active and inactive) The modal should auto-close after 10 seconds for all completed workflows
  useEffect(() => {
    // Clear any existing timer
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }

    // Run countdown for ANY workflow that has completed steps (active or inactive)
    if (areStepsCompleted && !hasError) {
      setCountdown(10);
      countdownTimerRef.current = setInterval(() => {
        setCountdown((prev) => {
          const newCount = prev - 1;
          
          if (newCount <= 0) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
            if (handleCloseRef.current) {
              handleCloseRef.current();
            }
            return 0;
          }
          return newCount;
        });
      }, 1000);
    } else {
      // Reset countdown if not in completed state
      setCountdown(10);
    }

    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [areStepsCompleted, hasError, modalWorkflowSessionId, isActiveWorkflow, forceMinimized]);

  // Update minimized state when forceMinimized prop changes CRITICAL: Only force minimize if it's an inactive workflow AND user hasn't manually maximized it Allow inactive workflows to be maximized if user clicks maximize button
  useEffect(() => {
    // Only force minimize if forceMinimized is true AND it's not the active workflow AND user hasn't explicitly maximized it
    if (forceMinimized && !isActiveWorkflow && !userMaximizedInactiveRef.current) {
      // Only set to minimized if user hasn't explicitly maximized it
      setIsMinimized(true);
    } else if (!forceMinimized || isActiveWorkflow) {
      // Reset the flag when workflow becomes active or forceMinimized becomes false
      userMaximizedInactiveRef.current = false;
    }
  }, [forceMinimized, isActiveWorkflow, setIsMinimized]);

  // CRITICAL: When this modal becomes active (forceMinimized changes from true to false OR becomes active workflow), and it's processing, ensure it's maximized (not minimized)
  useEffect(() => {
    // When workflow becomes active and modal is not forced minimized, maximize if processing
    if (!forceMinimized && isActiveWorkflow && isProcessing) {
      setIsMinimized(false);
    }
  }, [forceMinimized, isActiveWorkflow, isProcessing, setIsMinimized]);

  // Auto-unminimize if processing stops or error occurs Also unminimize when stream completes (unless forced minimized) CRITICAL: Track previous values to prevent infinite loops
  const prevAreStepsCompletedRef = useRef(areStepsCompleted);
  const prevIsProcessingRefForMinimized = useRef(isProcessing);
  const prevHasErrorRef = useRef(hasError);
  
  useEffect(() => {
    // Only update if values actually changed
    const stepsCompletedChanged = prevAreStepsCompletedRef.current !== areStepsCompleted;
    const isProcessingChanged = prevIsProcessingRefForMinimized.current !== isProcessing;
    const hasErrorChanged = prevHasErrorRef.current !== hasError;
    
    prevAreStepsCompletedRef.current = areStepsCompleted;
    prevIsProcessingRefForMinimized.current = isProcessing;
    prevHasErrorRef.current = hasError;
    
    if (!stepsCompletedChanged && !isProcessingChanged && !hasErrorChanged) {
      return; // Nothing changed, don't update
    }
    
    // CRITICAL: When stream completes, always maximize (unless force minimized for inactive workflows) For active workflows, always maximize on completion For inactive workflows (forceMinimized), only maximize if it's the active workflow
    if (areStepsCompleted) {
      // If it's the active workflow, always maximize on completion
      if (isActiveWorkflow || !forceMinimized) {
        setIsMinimized(false);
      }
    } else if (!forceMinimized && (!isProcessing || hasError)) {
      // Also maximize on errors or when processing stops (check current state, don't rely on isMinimized in deps)
      setIsMinimized(false);
    }
  }, [areStepsCompleted, isProcessing, hasError, forceMinimized, setIsMinimized]);

  // Initialize jobId from initialJobId if not set and we have it Also update currentStep when jobId is set (we're past "adding files" step)
  useEffect(() => {
    if (initialJobId && !jobId) {
      setJobId(initialJobId);
      // If we have a jobId, we're already processing documents
      setCurrentStep(1);
      setCompletedSteps([0]);
      setProcessingStatus("processing");
    }
  }, [initialJobId, jobId]);
  
  // Update step when jobId is set (from stream or API)
  useEffect(() => {
    if (jobId && currentStep === 0) {
      // If jobId exists and we're still on step 0, move to step 1
      setCurrentStep(1);
      setCompletedSteps([0]);
      if (!processingStatus) {
        setProcessingStatus("processing");
      }
    }
  }, [jobId, currentStep, processingStatus]);
  
  // OPTIMIZED: Use centralized utility to determine if modal should show
  const visibility = useMemo(() => shouldShowProcessingModal({
    workflowSessionId: modalWorkflowSessionId,
    isActiveWorkflow,
    workflowSessions,
    isProcessing,
  }), [modalWorkflowSessionId, isActiveWorkflow, workflowSessions, isProcessing]);
  
  // CRITICAL: Check BOTH local areStepsCompleted AND workflowStepsCompleted from session state This prevents completed workflows from showing the modal (except for countdown)
  const isCompleted = areStepsCompleted || workflowStepsCompleted;
  
  // CRITICAL: Final check - don't show if completed This is a safety check to prevent completed workflows from showing
  const shouldShow = !isCompleted && visibility.shouldShow;
  
  // OPTIMIZED: Use centralized utility to get comprehensive workflow state
  const comprehensiveState = useMemo(() => getComprehensiveWorkflowState({
    workflowSessionId: modalWorkflowSessionId,
    isActiveWorkflow,
    workflowSessions,
    isProcessing,
    initialJobId,
  }), [modalWorkflowSessionId, isActiveWorkflow, workflowSessions, isProcessing, initialJobId]);
  
  // Extract commonly used values from comprehensive state
  const hasData = comprehensiveState?.hasData || false;
  const isWaiting = comprehensiveState?.isWaiting || false;
  const queuePosition = comprehensiveState?.queuePosition;
  const totalInQueue = comprehensiveState?.totalInQueue;
  
  // Don't render anything if should not show
  if (!shouldShow) {
    return null;
  }

  // Minimized view - only for inactive workflows (forceMinimized=true) Active workflow modal should hide when minimized, not show in minimized stack If active workflow is minimized, just render hidden JobStreamDisplay and return
  if (!forceMinimized && isMinimized && !hasError && modalWorkflowSessionId) {
    // Active workflow is minimized — parent MultiProcessingModalManager keeps the stream when useExternalJobStream
    return (
      <>
        {!useExternalJobStream && jobId && (
          <div style={{ display: 'none' }}>
            <JobStreamDisplay 
              jobId={jobId} 
              onStatusUpdate={handleStatusUpdate}
            />
          </div>
        )}
      </>
    );
  }
  
  const shouldShowMinimized = forceMinimized && isMinimized !== false;
  
  if (shouldShowMinimized && !hasError && modalWorkflowSessionId) {
    // When forceMinimized is true, the parent container handles positioning with flex So we don't need any positioning classes - just let it be a normal flex child
    return (
      <>
        {/* Always render JobStreamDisplay (hidden) to detect completion even when minimized */}
        {!useExternalJobStream && jobId && (
          <div style={{ display: 'none' }}>
            <JobStreamDisplay 
              jobId={jobId} 
              onStatusUpdate={handleStatusUpdate}
            />
          </div>
        )}
        <div className="bg-white border-container shadow-xl rounded-2xl max-w-xs">
          <div className="px-6 py-4">
            <div className="flex flex-col gap-3">
              {/* Header with workflow name, queue position, and maximize button */}
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="text-xs text-gray-500 font-medium">{isSpanish ? 'Workflow' : 'Workflow'}:</span>
                    <span className="font-semibold text-sm truncate text-primary">{workflowName}</span>
                  </div>
                  {queuePosition && totalInQueue > 1 && (
                    <span className="text-xs text-gray-500 font-medium mt-1">
                      {queuePosition} / {totalInQueue}
                    </span>
                  )}
                </div>
                {!forceMinimized && (
            <button
              onClick={() => setIsMinimized(false)}
              className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0 ml-2"
              title={isSpanish ? 'Maximizar' : 'Maximize'}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
                )}
                {forceMinimized && !isActiveWorkflow && (
                  <>
                    <button
                      onClick={() => {
                        // Allow maximizing inactive workflow modal without switching workflows User can view the modal in full screen even if it's from another workflow
                        userMaximizedInactiveRef.current = true;
                        setIsMinimized(false);
                      }}
                      className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0 ml-2"
                      title={isSpanish ? 'Maximizar' : 'Maximize'}
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                    {areStepsCompleted && (
                      <button
                        onClick={handleClose}
                        className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0 ml-2"
                        title={isSpanish ? 'Cerrar' : 'Close'}
                      >
                        <img src={closeIcon} alt="Close" className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
              
              {/* Steps displayed vertically - only show if not waiting */}
              {!isWaiting ? (
                <div className="flex flex-col gap-4">
                  {processingSteps.map((step, index) => {
                    const isCompleted = completedSteps.includes(index);
                    const isActive = index <= currentStep;

                    if (!isActive) return null;

                    return (
                      <div key={index} className="flex items-center gap-2">
                        {isCompleted ? (
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0"></div>
                        )}
                        <span className={`text-xs ${!isCompleted && index === currentStep ? "text-primary font-medium" : "text-gray-600"}`}>
                          {step}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : isWaiting ? (
                /* Waiting state for minimized view - only show if actually waiting */
                <div className="flex flex-col items-center py-4">
                  <div className="w-12 h-12 mb-2 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs text-gray-600 text-center">
                    {isSpanish ? 'Esperando en cola...' : 'Waiting in queue...'}
                  </span>
                </div>
              ) : (
                /* No steps to show - workflow might be in transition */
                <div className="flex flex-col items-center py-4">
                  <div className="w-12 h-12 mb-2 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs text-gray-600 text-center">
                    {isSpanish ? 'Procesando...' : 'Processing...'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // Full modal view
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-999">
      <div className={`bg-white p-8 rounded-xl shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto flex flex-col relative ${areStepsCompleted ? '' :  `${!hasError && isWaiting && !isCompleted && (queuePosition && totalInQueue > 1) ? '' : 'h-[90vh]'}`}`}>
        <div className='flex justify-between items-center mb-4'>
          {/* {queuePosition && totalInQueue > 1 && (
            <span className="text-sm text-gray-500 font-medium">
              {queuePosition} / {totalInQueue}
            </span>
          )} */}
          <p className="text-sm text-gray-500 text-center font-medium">
          {workflowName}
            </p>
          <div className='flex justify-end gap-2 ml-auto'>
            {/* Always show minimize button when modal is maximized */}
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
              title={isSpanish ? 'Minimizar' : 'Minimize'}
            >
              <Minimize2 className="w-5 h-5" />
            </button>
            {/* When steps are completed, always use handleClose (not cancel) */}
            <img
              className='h-6 w-6 cursor-pointer'
              src={closeIcon}
              onClick={areStepsCompleted ? handleClose : handleCancel}
              alt={areStepsCompleted ? "Close" : "Cancel"}
              title={areStepsCompleted ? (isSpanish ? "Cerrar" : "Close") : (isSpanish ? "Cancelar procesamiento" : "Cancel processing")}
            />
          </div>
        </div>
        <div className="flex flex-col items-center mb-6">
          {hasError ? (
            <AlertTriangle className="p-4 h-24 w-24 mb-4 text-red-500 stroke-red-500" />
          ) : (
            !hasError && isWaiting && !isCompleted ? (
              <div className="w-16 h-16 mb-8 mt-6 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <img
                className="h-24 w-24 mb-4"
                src={areStepsCompleted ? checkIcon : jsonAnimation}
                alt={areStepsCompleted ? "Process Complete" : "Processing"}
              />
            )
          )}
          <h2 className="text-2xl font-bold text-center">
            {hasError 
              ? getProcessingModalTranslation('processingError', isSpanish) 
              : areStepsCompleted 
              ? getProcessingModalTranslation('documentsProcessed', isSpanish) 
              : isWaiting && !isCompleted
              ? (isSpanish ? 'En cola de procesamiento' : 'In processing queue')
              : getProcessingModalTranslation('processingDocumentsTitle', isSpanish)}
          </h2>
          {hasError && (
            <p className="mt-2 text-gray-500 text-center">{errorMessage}</p>
          )}
          {isWaiting && !isCompleted && (
            <p className="mt-2 text-gray-500 text-center">
              {isSpanish 
                ? 'Esperando a que termine el workflow anterior...'
                : 'Waiting for previous workflow to finish...'}
            </p>
          )}
        </div>

        {/* Show waiting state - only if not completed */}
        {/* {!hasError && isWaiting && !isCompleted && (
          <div className="flex flex-col items-center mb-6">
            <div className="w-24 h-24 mb-4 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <h3 className="text-lg font-semibold text-center mb-2">
              {isSpanish ? 'Esperando en cola...' : 'Waiting in queue...'}
            </h3>
            <p className="text-gray-500 text-center text-sm">
              {isSpanish 
                ? 'Este workflow está en la cola de procesamiento. Se iniciará automáticamente cuando termine el workflow anterior.'
                : 'This workflow is in the processing queue. It will start automatically when the previous workflow finishes.'}
            </p>
            {queuePosition && totalInQueue > 1 && (
              <p className="mt-2 text-sm text-gray-400">
                {isSpanish 
                  ? `Posición en cola: ${queuePosition} de ${totalInQueue}`
                  : `Queue position: ${queuePosition} of ${totalInQueue}`}
              </p>
            )}
          </div>
        )} */}

        {!hasError && !isWaiting && (
          <div className="space-y-2 flex-1 flex flex-col">
            {processingSteps.map((step, index) => {
              const isCompleted = completedSteps.includes(index);
              const isActive = index <= currentStep;

              if (!isActive) return null;

              return (
                <div key={index} className="flex items-center py-2">
                  {isCompleted ? (
                    <CheckCircle className="mr-3 w-5 h-5 stroke-primary text-green-500 flex-shrink-0" />
                  ) : (
                    <div className="mr-3 w-5 h-5 flex-shrink-0">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                  <span className={`${!isCompleted && index === currentStep
                    ? "text-primary font-medium animate-pulse" : "text-gray-700"}`}>
                    {step}
                  </span>
                </div>
              );
            })}
            
            {/* Job Stream Display — one SSE in MultiProcessingModalManager when useExternalJobStream */}
            {jobId && (
              <div style={{ display: areStepsCompleted ? 'none' : 'block' }}>
                {useExternalJobStream ? (
                  <JobStreamDisplay jobId={jobId} viewOnly />
                ) : (
                  <JobStreamDisplay
                    jobId={jobId}
                    onStatusUpdate={handleStatusUpdate}
                  />
                )}
              </div>
            )}
            
            {/* Show countdown for ALL completed workflows (active and inactive) */}
            {areStepsCompleted && (
              <div className="mt-8 text-center flex-1 flex items-end justify-end justify-center">
                <p className="mt-8 text-sm text-gray-600 text-left">
                  {isSpanish ? `Cerrando automáticamente en ${countdown} segundos...` : `Closing automatically in ${countdown} seconds...`}
                </p>
              </div>
            )}
          </div>
        )}


        {hasError && (
          <div className="mt-8 flex justify-center">
            <button
              onClick={handleRetry}
              disabled={isApiCallInProgress}
              className="bg-default hover:bg-primary-dark text-white py-2 px-4 rounded-md flex items-center"
            >
              {isApiCallInProgress ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {getProcessingModalTranslation('retrying', isSpanish)}
                </>
              ) : (
                getProcessingModalTranslation('retryProcessing', isSpanish)
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};